// Uploads through file-stc: request → PUT the bytes straight to S3 (presigned) →
// confirm. file-stc decides between a single PUT and a multipart plan (today:
// multipart above 16 MB for videos, 64 MB for images); the client just follows the
// plan. Parts go up 4 at a time, each retried, and an interrupted upload resumes
// from the parts S3 already has (GET /api/files/{id}/upload).
import client from "./client";
import { rememberFile, type FileInfo } from "./files";
import { withoutGps } from "../lib/jpegGps";

export interface RequestUploadInput {
  filename: string;
  /** Type of the bytes actually uploaded (the re-encoded output, if any). */
  mime: string;
  size: number;
  width?: number;
  height?: number;
  durationMs?: number;
  /** Makes this file the poster of that (still PENDING) video. */
  thumbnailFor?: string;
  /** PRIVATE (documents only): readable through a signed link, never a public URL. Absent = PUBLIC. */
  visibility?: "PRIVATE";
}

export interface SingleUpload {
  method: "PUT";
  url: string;
  headers: Record<string, string>;
}

export interface PartUrl {
  n: number;
  url: string;
}

export interface MultipartPlan {
  uploadId: string;
  partSize: number;
  parts: PartUrl[];
}

export interface UploadPlan {
  id: string;
  url: string;
  upload?: SingleUpload;
  multipart?: MultipartPlan;
}

export interface UploadedPart {
  n: number;
  etag: string;
}

export type ProgressFn = (sentBytes: number, totalBytes: number) => void;

export interface UploadOptions {
  onProgress?: ProgressFn;
  signal?: AbortSignal;
}

export const PART_CONCURRENCY = 4;
export const PART_RETRIES = 3;
let retryDelayMs = 500;

/** Test hook: no back-off between part retries. */
export function setRetryDelayForTests(ms: number): void {
  retryDelayMs = ms;
}

export async function requestUpload(
  input: RequestUploadInput,
): Promise<UploadPlan> {
  const { data } = await client.post<{ result: UploadPlan }>(
    "/api/files",
    input,
  );
  const plan = data.result;
  if (!plan?.id || (!plan.upload && !plan.multipart)) {
    throw new Error("Upload request failed: no upload plan in response");
  }
  return plan;
}

/** Missing parts of an open multipart upload (owner only). */
export async function resumeUpload(fileId: string): Promise<MultipartPlan> {
  const { data } = await client.get<{ result: MultipartPlan }>(
    `/api/files/${encodeURIComponent(fileId)}/upload`,
  );
  return data.result;
}

/** Marks the file READY. Single PUTs confirm with no parts; a resumed multipart
 *  may also send none (file-stc then uses S3's own part list). */
export async function confirm(
  fileId: string,
  parts: UploadedPart[] = [],
): Promise<FileInfo> {
  const { data } = await client.post<{ result: FileInfo }>(
    `/api/files/${encodeURIComponent(fileId)}/confirm`,
    { parts },
  );
  rememberFile(data.result);
  return data.result;
}

/** PUT with exactly the presigned headers. Content-Length is the browser's,
 *  from the Blob (a forbidden header for scripts). */
export async function uploadSingle(
  upload: SingleUpload,
  body: Blob,
  opts: UploadOptions = {},
): Promise<void> {
  const headers = Object.fromEntries(
    Object.entries(upload.headers ?? {}).filter(
      ([k]) => k.toLowerCase() !== "content-length",
    ),
  );
  const res = await fetch(upload.url, {
    method: upload.method ?? "PUT",
    headers,
    body,
    signal: opts.signal,
  });
  if (!res.ok) {
    throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`);
  }
  opts.onProgress?.(body.size, body.size);
}

function partRange(n: number, partSize: number, total: number) {
  const start = (n - 1) * partSize;
  return { start, end: Math.min(start + partSize, total) };
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (ms <= 0) return resolve();
    const t = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

async function putPart(
  part: PartUrl,
  body: Blob,
  signal: AbortSignal,
): Promise<string> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= PART_RETRIES; attempt++) {
    if (attempt > 0) await wait(retryDelayMs * attempt, signal);
    try {
      const res = await fetch(part.url, { method: "PUT", body, signal });
      if (!res.ok) throw new Error(`part ${part.n}: ${res.status}`);
      // S3 quotes the ETag; the bucket CORS rule exposes it.
      const etag = res.headers.get("ETag");
      if (!etag) throw new Error(`part ${part.n}: no ETag (CORS expose?)`);
      return etag;
    } catch (err) {
      if (isAbort(err) || signal.aborted) throw err;
      lastErr = err;
    }
  }
  throw lastErr;
}

/**
 * PUTs every part in `plan.parts` (only the missing ones, after a resume),
 * PART_CONCURRENCY at a time. Progress counts parts S3 already holds as sent.
 */
export async function uploadMultipart(
  plan: MultipartPlan,
  body: Blob,
  onProgress?: ProgressFn,
  signal?: AbortSignal,
): Promise<UploadedPart[]> {
  const total = body.size;
  const todo = [...plan.parts].sort((a, b) => a.n - b.n);
  let sent =
    total -
    todo.reduce((sum, p) => {
      const { start, end } = partRange(p.n, plan.partSize, total);
      return sum + (end - start);
    }, 0);
  onProgress?.(sent, total);

  const ctrl = new AbortController();
  const onAbort = () => ctrl.abort();
  signal?.addEventListener("abort", onAbort, { once: true });
  if (signal?.aborted) ctrl.abort();

  const done: UploadedPart[] = [];
  let next = 0;
  async function worker() {
    while (next < todo.length && !ctrl.signal.aborted) {
      const part = todo[next++];
      const { start, end } = partRange(part.n, plan.partSize, total);
      const etag = await putPart(part, body.slice(start, end), ctrl.signal);
      done.push({ n: part.n, etag });
      sent += end - start;
      onProgress?.(sent, total);
    }
  }
  try {
    await Promise.all(
      Array.from({ length: Math.min(PART_CONCURRENCY, todo.length) }, () =>
        worker().catch((err) => {
          ctrl.abort(); // one part gave up: stop the others
          throw err;
        }),
      ),
    );
  } finally {
    signal?.removeEventListener("abort", onAbort);
  }
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  return done.sort((a, b) => a.n - b.n);
}

// --- resume across reloads (best effort) -----------------------------------

const RESUME_PREFIX = "fileUpload:v1:";

// Visibility is part of the key: a PRIVATE upload must never resume a PUBLIC one of the same file (BOOK_PDF_NOT_PRIVATE).
function resumeKey(body: Blob, filename: string, mime: string, visibility?: "PRIVATE"): string | null {
  if (!(body instanceof File)) return null;
  return `${RESUME_PREFIX}${filename}:${body.size}:${body.lastModified}:${mime}:${visibility ?? "PUBLIC"}`;
}

function readResume(key: string | null): { fileId: string } | null {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as { fileId: string }) : null;
  } catch {
    return null;
  }
}

function writeResume(key: string | null, fileId: string, uploadId: string) {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify({ fileId, uploadId }));
  } catch {
    // storage full / blocked: the upload just cannot resume after a reload
  }
}

function clearResume(key: string | null) {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** Uploads the bytes of an already-requested plan (no confirm). */
export async function putPlan(
  plan: UploadPlan,
  body: Blob,
  opts: UploadOptions = {},
): Promise<UploadedPart[]> {
  if (plan.upload) {
    await uploadSingle(plan.upload, body, opts);
    return [];
  }
  return uploadMultipart(plan.multipart!, body, opts.onProgress, opts.signal);
}

export type UploadMeta = Omit<RequestUploadInput, "size">;

/**
 * request → PUT → confirm; resolves with the READY file only after confirm.
 * A multipart upload of the same File interrupted earlier (even before a
 * reload) resumes from the parts S3 already has.
 */
export async function uploadFile(
  body: Blob,
  meta: UploadMeta,
  opts: UploadOptions = {},
): Promise<FileInfo> {
  const key = resumeKey(body, meta.filename, meta.mime, meta.visibility);
  const saved = readResume(key);
  if (saved) {
    let plan: MultipartPlan | null = null;
    try {
      plan = await resumeUpload(saved.fileId);
    } catch {
      clearResume(key); // gone, aborted by lifecycle, or already READY elsewhere
    }
    if (plan) {
      await uploadMultipart(plan, body, opts.onProgress, opts.signal);
      const info = await confirm(saved.fileId, []);
      clearResume(key);
      return info;
    }
  }

  const plan = await requestUpload({ ...meta, size: body.size });
  if (plan.multipart) writeResume(key, plan.id, plan.multipart.uploadId);
  const parts = await putPlan(plan, body, opts);
  const info = await confirm(plan.id, parts);
  clearResume(key);
  return info;
}

/** An image's pixel size (empty when the browser cannot decode it). */
export async function imageSize(
  file: Blob,
): Promise<{ width?: number; height?: number }> {
  try {
    const bitmap = await createImageBitmap(file);
    const out = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return out;
  } catch {
    return {};
  }
}

/**
 * The image types the web uploads, as-is (file-stc IMAGE types). HEIC/HEIF go up unchanged: the
 * viewer decodes them where the browser can't (lib/heicImage). SVG is checked by file-stc at confirm
 * (a hostile one gets 422) and its displayable URL is the confirm answer's.
 */
const WEB_IMAGE_TYPES: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  heic: "image/heic",
  heif: "image/heif",
  svg: "image/svg+xml",
};

/** `accept` for every image input (avatar, chat, wall, books, offers). Extensions too: some OSes type a .heic as "". */
export const IMAGE_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,image/svg+xml,.heic,.heif,.svg";

/** Avatars stay JPEG, PNG or WebP (owner 2026-11-01): a small square every client shows. */
export const AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const AVATAR_ACCEPT = AVATAR_TYPES.join(",");

/** Videos chat and the post editor take (file-stc VIDEO types). */
export const VIDEO_ACCEPT = "video/mp4,video/quicktime,video/webm";

/** `accept` for the chat attachment pickers: photos and videos. */
export const MEDIA_ACCEPT = `${IMAGE_ACCEPT},${VIDEO_ACCEPT}`;

/**
 * A file's own type when it says what the file is. Empty and `application/octet-stream` (some OS /
 * browser setups type a .mov so) say nothing: the caller goes by the extension instead.
 */
export function ownTypeOf(file: File): string {
  const own = (file.type || "").toLowerCase();
  return own === "application/octet-stream" ? "" : own;
}

/** A picked file is a video (its own type, else its extension), whether or not we can send it. */
export function isVideoFile(file: File): boolean {
  const own = ownTypeOf(file);
  if (own) return own.startsWith("video/");
  return /\.(mp4|m4v|mov|webm|mkv|avi|wmv|flv|3gp|3g2|mpe?g|ts|ogv)$/i.test(file.name);
}

/**
 * A video we can send (file-stc VIDEO types: MP4, MOV, WebM), by its own type, else its extension.
 * Anything else is refused up front with "video_type_not_supported", before any upload.
 */
export function isSupportedVideoFile(file: File): boolean {
  const own = ownTypeOf(file);
  if (own) return VIDEO_ACCEPT.split(",").includes(own);
  return /\.(mp4|m4v|mov|webm)$/i.test(file.name);
}

/**
 * The type to declare for an image: its own type when it is one we take, else (an empty or
 * application/octet-stream type) the one its extension names. null for anything else, so the
 * declared type always matches the bytes.
 */
export function imageMimeOf(file: File): string | null {
  // An iPhone burst / Live Photo HEIC may be typed image/heic-sequence: file-stc takes it as HEIC.
  const own = ownTypeOf(file).replace(/^image\/(heic|heif)-sequence$/, "image/$1");
  if (own) return Object.values(WEB_IMAGE_TYPES).includes(own) ? own : null;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return WEB_IMAGE_TYPES[ext] ?? null;
}

/** A picked file an avatar may be (by its type, else its extension; see imageMimeOf). */
export function isAvatarImage(file: File): boolean {
  const mime = imageMimeOf(file);
  return mime != null && AVATAR_TYPES.includes(mime);
}

export class UnsupportedImageError extends Error {
  constructor(name: string) {
    super(`unsupported image type: ${name}`);
    this.name = "UnsupportedImageError";
  }
}

export const SVG_MIME = "image/svg+xml";
/** file-stc's SVG cap (413 above it): refused here first, with its own message. */
export const MAX_SVG_BYTES = 1_048_576;

export class SvgTooLargeError extends Error {
  constructor(name: string) {
    super(`SVG over ${MAX_SVG_BYTES} bytes: ${name}`);
    this.name = "SvgTooLargeError";
  }
}

/** An SVG (by type or extension, see imageMimeOf) over MAX_SVG_BYTES. Sync: for pickers that check at once. */
export function isOversizedSvg(file: File): boolean {
  return imageMimeOf(file) === SVG_MIME && file.size > MAX_SVG_BYTES;
}

async function headText(file: Blob, bytes: number): Promise<string> {
  const head = file.slice(0, bytes);
  if (typeof head.text === "function") return head.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(head);
  });
}

/** The first bytes are SVG markup (an optional BOM, XML prolog, comments, doctype, then `<svg`). */
export async function sniffsAsSvg(file: Blob): Promise<boolean> {
  try {
    const head = (await headText(file, 1024)).replace(/^\uFEFF/, "").trimStart();
    return head.startsWith("<") && /<svg[\s>/]/i.test(head);
  } catch {
    return false;
  }
}

/**
 * Uploads an image as-is (declared type = the file's own type, see imageMimeOf). A file with no
 * type whose bytes are SVG is declared SVG whatever its name. An SVG over MAX_SVG_BYTES is refused
 * before any request (SvgTooLargeError). A JPEG's GPS tags are removed first (lib/jpegGps).
 */
export async function uploadImage(
  file: File,
  opts: UploadOptions & { width?: number; height?: number } = {},
): Promise<FileInfo> {
  let mime = imageMimeOf(file);
  if (!ownTypeOf(file) && mime !== SVG_MIME && (await sniffsAsSvg(file))) mime = SVG_MIME;
  if (!mime) throw new UnsupportedImageError(file.name);
  if (mime === SVG_MIME && file.size > MAX_SVG_BYTES) throw new SvgTooLargeError(file.name);
  // A JPEG goes up without its GPS tags (same bytes otherwise); other types as picked.
  const body = mime === "image/jpeg" ? await withoutGps(file) : file;
  const size =
    opts.width && opts.height
      ? { width: opts.width, height: opts.height }
      : await imageSize(file);
  return uploadFile(body, { filename: file.name, mime, ...size }, opts);
}

/** Where an interrupted video upload stands: the video file id (PENDING) and its confirmed poster's id. */
export interface VideoResume {
  videoId: string;
  posterId: string;
}

export interface VideoUploadMeta {
  /** Defaults to the File's own name / type. */
  filename?: string;
  mime?: string;
  durationMs?: number;
  width?: number;
  height?: number;
  posterWidth?: number;
  posterHeight?: number;
}

export interface VideoUploadOptions extends UploadOptions {
  /** Picks up an upload interrupted earlier (from `onResume`). */
  resume?: VideoResume;
  /** Called as the upload advances; keep the last value to resume after a failure. */
  onResume?: (state: VideoResume) => void;
}

/**
 * A video and its poster, following file-stc's plan for each (single PUT or multipart). The video
 * is requested first (file-stc needs its exact size), then the poster with `thumbnailFor` while the
 * video is still PENDING; the poster goes up and is confirmed, then the video's bytes, and the video
 * is confirmed last. `onProgress` counts the video's bytes. With `resume`, a multipart video uploads
 * only its missing parts and the confirmed poster is not sent again; a resume file-stc refuses starts
 * over. A failure before the poster is confirmed gives no resume state: the next try restarts. `poster` is null when a resumed upload reused an already-confirmed poster.
 */
export async function uploadVideoWithPoster(
  video: Blob,
  poster: Blob,
  meta: VideoUploadMeta = {},
  opts: VideoUploadOptions = {},
): Promise<{ video: FileInfo; poster: FileInfo | null; posterId: string }> {
  const { filename, mime, posterWidth, posterHeight, ...videoMeta } = meta;
  const name = filename ?? (video instanceof File ? video.name : "video.mp4");
  const type = mime ?? (video.type || "video/mp4");

  if (opts.resume?.posterId) {
    let missing: MultipartPlan | null = null;
    try {
      missing = await resumeUpload(opts.resume.videoId);
    } catch {
      missing = null; // single PUT, gone, or already aborted: start over
    }
    if (missing?.parts) {
      await uploadMultipart(missing, video, opts.onProgress, opts.signal);
      const videoInfo = await confirm(opts.resume.videoId, []);
      return { video: videoInfo, poster: null, posterId: opts.resume.posterId };
    }
  }

  const videoPlan = await requestUpload({ filename: name, mime: type, size: video.size, ...videoMeta });
  // The resume state is saved only once the poster is confirmed: file-stc links the poster when it is
  // requested and refuses a second one (409), so a failed poster step restarts the whole send.
  const posterInfo = await sendPoster(poster, videoPlan.id, posterWidth, posterHeight, opts.signal);
  opts.onResume?.({ videoId: videoPlan.id, posterId: posterInfo.id });
  const videoParts = await putPlan(videoPlan, video, opts);
  const videoInfo = await confirm(videoPlan.id, videoParts);
  return { video: videoInfo, poster: posterInfo, posterId: posterInfo.id };
}

async function sendPoster(
  poster: Blob,
  videoId: string,
  width: number | undefined,
  height: number | undefined,
  signal: AbortSignal | undefined,
): Promise<FileInfo> {
  const size = width && height ? { width, height } : await imageSize(poster);
  const posterMime = poster.type || "image/jpeg";
  const posterExt = posterMime.split("/")[1]?.replace("jpeg", "jpg") ?? "jpg";
  const plan = await requestUpload({
    filename: `poster.${posterExt}`,
    mime: posterMime,
    size: poster.size,
    thumbnailFor: videoId,
    ...size,
  });
  const parts = await putPlan(plan, poster, { signal });
  return confirm(plan.id, parts);
}
